// ============================================================
// PERRY — THE RUN OVERLAY
// (Still named QTEOverlay because it is still the run's DOM
//  layer — but there is no longer a QTE prompt anywhere in it.)
//
// THE RULE THIS FILE NOW EXISTS TO ENFORCE, 2026-07-26:
//   NOTHING THAT TELLS YOU WHAT TO PRESS MAY LIVE ON THE GLASS.
//   No key glyph. No closing ring. No preview letters. If a
//   player needs to know where a strike is coming from, they
//   look at the CREATURE — the lit limb, the coil, the ember arc
//   growing toward them. All of that lives in the 3D scene, in
//   CinematicStage, made of the same fire the foe is made of.
//
// What is allowed up here: your own vitals, your chain, what
// your Imperas cost, the outcome of a beat that already happened,
// and the story. Never the future. Reading the future is the
// game.
//
// The one exception is deliberate: the grade flash. It reports a
// beat that is already over and cannot help you cheat the next
// one — and without it a PERFECT and a GOOD feel identical, which
// would flatten the whole skill curve.
// ============================================================

import { useEffect, useRef, useState } from "react";
import { GM } from "./theme";
import { KeyGlyph, StatPips, ensureKeyframes, MetalRule } from "./PerryUI";
import { QTE, REGALIA, GUARD, SIGHT, TUTORIAL, hueOf, slotKey } from "./perryData";

const KF = `
@keyframes perryGradePop {
  0%   { transform: scale(0.6) translateY(6px); opacity: 0; }
  22%  { transform: scale(1.14) translateY(0); opacity: 1; }
  70%  { transform: scale(1) translateY(0); opacity: 1; }
  100% { transform: scale(1.02) translateY(-14px); opacity: 0; }
}
@keyframes perryChainPulse {
  0%   { transform: scale(1.5); opacity: 0; }
  40%  { transform: scale(1); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
@keyframes perrySubIn {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
/* ── WINDED, 2026-07-29 ──
   Deliberately a slow breathing pulse rather than a fast alarm
   flash: you are not being warned, you are being TOLD that the
   next couple of seconds are not yours. Panic-red strobing would
   make a player mash, and mashing is exactly the thing this state
   exists to punish. */
@keyframes perryWinded {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.45; }
}
@keyframes perryHurt {
  0%   { opacity: 0; }
  18%  { opacity: 0.55; }
  100% { opacity: 0; }
}
@keyframes perryOpenIn {
  0%   { opacity: 0; letter-spacing: 22px; }
  40%  { opacity: 1; letter-spacing: 9px; }
  100% { opacity: 0; letter-spacing: 9px; }
}
@keyframes perryEmpIn {
  0%   { opacity: 0; transform: scale(1.06); }
  100% { opacity: 1; transform: scale(1); }
}
@keyframes perryEmpTitle {
  0%   { opacity: 0; letter-spacing: 30px; }
  30%  { opacity: 1; letter-spacing: 11px; }
  78%  { opacity: 1; letter-spacing: 11px; }
  100% { opacity: 0; letter-spacing: 11px; }
}
/* ── THE REPORT CARD, 2026-07-30 ──
   Slow and heavy on the way in. The road has stopped; the card
   should feel like it stopped it, not like it slid over the top
   of something still moving. */
@keyframes perryCardVeil {
  from { opacity: 0; }
  to   { opacity: 1; }
}
@keyframes perryCardIn {
  0%   { opacity: 0; transform: translateY(22px) scale(0.965); }
  100% { opacity: 1; transform: translateY(0) scale(1); }
}
/* The tally counts itself in. One number, arriving with weight,
   because it is the number the whole road exists to show you. */
@keyframes perryTallyIn {
  0%   { opacity: 0; transform: scale(1.6); }
  55%  { opacity: 1; transform: scale(0.94); }
  100% { opacity: 1; transform: scale(1); }
}
@keyframes perrySkipPulse {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.4; }
}
`;

const GRADE_STYLE = {
  PERFECT:  { c: "#4ff0dc", label: "PERFECT",  sub: "clean through" },
  GOOD:     { c: "#f0984a", label: "GOOD",     sub: "turned aside" },
  HELD:     { c: "#c0c4c8", label: "HELD",     sub: "it was a lie" },
  FRACTURE: { c: "#9fe8ff", label: "FRACTURE", sub: "it has come apart" },
  MISS:     { c: "#f4726a", label: "MISS",     sub: "" },
};

// Who is speaking over the road, and what that looks like. The
// Sheathe is gold and grand, the blade is cold and dry, and the
// world is neither and does not get a name plate.
const VOICE = {
  sheathe: { tint: "#ffe6a8", label: "THE SHEATHE" },
  blade:   { tint: "#b8c4cc", label: "THE BLADE" },
  world:   { tint: "#8a8078", label: null },
};

export default function QTEOverlay({ director }) {
  ensureKeyframes("perry-qte-keyframes", KF);
  const { grade, hud, line, treasure } = director;
  const [, tick] = useState(0);
  const hurtRef = useRef(null);

  // one cheap repaint per graded beat
  useEffect(() => { tick((n) => n + 1); }, [grade]);

  // red flash on a miss — imperative, never a state animation
  useEffect(() => {
    if (grade?.g === "MISS" && hurtRef.current) {
      const el = hurtRef.current;
      el.style.animation = "none";
      // reflow, then re-run — the standard restart trick
      void el.offsetWidth;
      el.style.animation = "perryHurt 0.7s ease-out";
    }
  }, [grade]);

  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 40, fontFamily: GM.fontUI }}>
      {/* ── THE EMPERIUM ── the road, briefly, is yours ── */}
      <EmperiumVeil director={director} />

      {/* ── hurt flash ── */}
      <div ref={hurtRef} style={{
        position: "absolute", inset: 0, opacity: 0,
        background: "radial-gradient(ellipse at 50% 60%, transparent 30%, rgba(180,30,20,0.85) 100%)",
      }} />

      {/* ── TOP LEFT — vitals ── */}
      <div style={{
        position: "absolute", top: 66, left: 24,
        display: "flex", flexDirection: "column", gap: 7,
      }}>
        <Vital label="ANIMA" hint="life · and a currency">
          <StatPips value={hud.anima} max={Math.max(hud.anima, director.animaCap)} full={GM.rose} empty={GM.roseDim} glyph="❤" size={17} />
        </Vital>
        <Vital label="CHAO" hint="breath · refills at a fire, not at a foe">
          <StatPips value={hud.chao} max={Math.max(hud.chao, director.maxChao)} full={GM.amethystBright} empty={GM.amethystDim} glyph="✦" size={16} />
        </Vital>
        {/* ══════════════════════════════════════════════════
            LYF — YOURS. BREATH, NOT SKIN. (2026-07-29)
            ══════════════════════════════════════════════════
            A landed answer never moves this. Only a miss does.
            At zero your paws stop listening, and the bar says so
            loudly — WINDED is the single most important word this
            overlay can put on screen, because while it is up
            everything the fen swings lands for free. */}
        <Vital label="LYF" hint="wind · only a MISS costs it">
          {hud.winded > 0 ? (
            <div style={{
              fontFamily: GM.font, letterSpacing: 2, fontSize: 14, fontWeight: 800,
              color: "#ff5a6a", textShadow: "0 0 14px #a8302a",
              animation: "perryWinded 0.55s ease-in-out infinite",
            }}>
              WINDED · {(hud.winded / 1000).toFixed(1)}s
            </div>
          ) : (
            <StatPips
              value={hud.lyf ?? 0}
              max={Math.max(1, hud.lyfMax ?? director.lyfCap ?? 1)}
              full="#7ce8d8" empty="#123a36" glyph="◇" size={15}
            />
          )}
        </Vital>
        <Vital label="EDGE" hint="your Lyt + your weapon · chips their Lyf">
          <span style={{
            color: hud.atv ? "#ffd08a" : GM.inkGoldBright, fontWeight: 800, fontSize: 16,
            textShadow: `0 0 10px ${hud.atv ? "#ffab58" : GM.inkGold}`,
          }}>
            {hud.edge ?? hud.lyt}{hud.atv ? ` 🔥+${hud.atv}` : ""}
          </span>
        </Vital>
        {treasure && (
          <div style={{ fontSize: 10, color: GM.vellumFaint, letterSpacing: 1.5, marginTop: 1 }}>
            {treasure.icon} {treasure.name.toUpperCase()}
          </div>
        )}
        {hud.slowLeft > 0 && (
          <div style={{ fontSize: 10, color: GM.amethystBright, letterSpacing: 1 }}>❄ STILLED · {hud.slowLeft}</div>
        )}
        {hud.foresight > 0 && (
          <div style={{ fontSize: 10, color: GM.amethystBright, letterSpacing: 1 }}>👁 SEEING · {hud.foresight}</div>
        )}
        {hud.raysArmed && (
          <div style={{ fontSize: 10, color: GM.vellumFaint, letterSpacing: 1 }}>🌅 RAYS OF HOPE · ARMED</div>
        )}
        {hud.capUsed && (
          <div style={{ fontSize: 10, color: GM.vellumFaint, letterSpacing: 1 }}>🎩 CAP SPENT</div>
        )}
        {/* ── THE LONG HOUR ── an ATV, so it says so, and says when
            it goes out. A flame the foe can snuff by withholding
            has to be visible or the counterplay is invisible. */}
        {hud.hourLit && (
          <div style={{ fontSize: 10, color: "#9fe8ff", letterSpacing: 1 }}>⏾ THE LONG HOUR · LIT</div>
        )}
        {hud.pw?.crownLeft > 0 && (
          <div style={{ fontSize: 10, color: GM.inkGoldBright, letterSpacing: 1 }}>♛ CROWN · 1 FREE</div>
        )}
        {hud.pw?.poolLeft > 0 && (
          <div style={{ fontSize: 10, color: GM.vellumFaint, letterSpacing: 1 }}>🪞 MIRROR POOL · 1</div>
        )}
        {hud.sight > 0 && (
          <div style={{ fontSize: 10, color: GM.inkGoldBright, letterSpacing: 1 }}>
            🜂 NAMES · {hud.sight}
          </div>
        )}
        {/* ── THE PURSE ──
            A report of what already happened, which is the one
            thing this layer is allowed to show. It cannot help you
            cheat the next beat; it can only tell you the road has
            been paying you, which is the entire point of it. */}
        {hud.kindling > 0 && (
          <div style={{ fontSize: 10, color: GM.amethystBright, letterSpacing: 1 }}>
            ⋔ {hud.kindling}
          </div>
        )}
      </div>

      {/* ── TOP RIGHT — the foe ── */}
      {hud.foe && (
        <div style={{ position: "absolute", top: 66, right: 24, textAlign: "right" }}>
          <div style={{
            fontFamily: GM.font, fontSize: 15, letterSpacing: 2,
            color: GM.vellum, textShadow: "0 1px 3px #000",
          }}>{hud.foe.name.toUpperCase()}</div>
          <div style={{ fontSize: 10, letterSpacing: 2, color: GM.vellumFaint, marginBottom: 5 }}>
            {hud.foe.rung.toUpperCase()}
            {hud.foeFractured && hud.foeLyfMax > 0 ? " · BREACHED" : ""}
          </div>
          {/* ── TWO BARS, IN THE ORDER YOU BREAK THEM ──
              LYF is the wall and it is a WIDTH, not pips: a
              fourteen-pip bar over T'yphiz's head would be a menu.
              ANIMA is the finish and it IS pips, because it is
              never more than five and each one is an event.

              THE WALL COMES BACK (2026-07-30). Lyf regenerates in
              full every time you take an Anima, so this bar is not
              a countdown — it refills, visibly, and the 0.22s ease
              sells it. BREACHED is a moment, not a state, which is
              why the label reads as an instruction and not as a
              condition. Fracture is a different word and belongs
              to surviving a Calamity.

              A basic enemy has no Lyf at all, so the wall simply
              is not drawn and the pips are the whole fight — the
              prologue husk looks exactly as simple as it is. */}
          {hud.foeLyfMax > 0 && (
            <div style={{ marginBottom: 5 }}>
              <div style={{
                height: 6, borderRadius: 3, overflow: "hidden",
                background: "#2a1410", border: "1px solid #4a2418",
              }}>
                <div style={{
                  height: "100%",
                  width: `${Math.max(0, Math.min(1, hud.foeLyf / hud.foeLyfMax)) * 100}%`,
                  background: hud.foeFractured
                    ? "linear-gradient(90deg,#4a2418,#6a3a24)"
                    : "linear-gradient(90deg,#8a8078,#d8dce0)",
                  boxShadow: hud.foeFractured ? "none" : "0 0 10px #d8dce0",
                  transition: "width 0.22s ease-out",
                }} />
              </div>
              <div style={{ fontSize: 9, letterSpacing: 2, color: hud.foeFractured ? "#8a5a3a" : "#b8bcc0", marginTop: 2 }}>
                {hud.foeFractured ? "OPEN — STRIKE THE THING ITSELF" : `LYF ${Math.max(0, hud.foeLyf)} / ${hud.foeLyfMax}`}
              </div>
            </div>
          )}
          <StatPips value={Math.max(0, hud.foeAnima)} max={hud.foeMax || 1} full="#e05a2a" empty="#3a1a10" glyph="◆" size={13} />
        </div>
      )}

      {/* ── CHAIN ── */}
      {/* ── THE STREAK — only shows once it is PAYING (2026-07-29)
          Below three it is not a thing yet, and a counter that
          spends most of its life reading "1" teaches nobody
          anything. It appears at the exact moment it starts
          putting ⋔ in your paw. */}
      {hud.streak >= 3 && (
        <div style={{
          position: "absolute", top: "22%", right: "6%", textAlign: "right",
          pointerEvents: "none",
        }}>
          <div style={{ fontSize: 9, letterSpacing: 3, color: "#ffd08a" }}>PERFECT STREAK</div>
          <div key={hud.streak} style={{
            fontFamily: GM.fontD, fontSize: 28, lineHeight: 1,
            color: "#ffd08a", textShadow: "0 0 20px #ffab58",
            animation: "perryGradePop 0.4s ease-out",
          }}>{hud.streak}</div>
        </div>
      )}

      {hud.chain > 0 && (
        <div key={hud.chain} style={{
          position: "absolute", top: 118, left: "50%", transform: "translateX(-50%)",
          textAlign: "center", animation: "perryChainPulse 0.3s ease-out",
        }}>
          <div style={{
            fontFamily: GM.fontD, fontSize: 34, fontWeight: 700, lineHeight: 1,
            color: hud.chain >= 30 ? "#4ff0dc" : GM.inkGoldBright,
            textShadow: `0 0 18px ${hud.chain >= 30 ? "#2bb8a8" : GM.inkGold}`,
          }}>{hud.chain}</div>
          <div style={{ fontSize: 9, letterSpacing: 4, color: GM.vellumFaint }}>UNBROKEN</div>
        </div>
      )}

      {/*
        ── WHERE THE PROMPT USED TO BE ──
        Nothing. On purpose. The strike is on the creature.
      */}

      {/* ── GRADE FLASH — a report, never a warning ── */}
      {grade && (
        <div key={grade.at} style={{
          position: "absolute", left: "50%", top: "38%", transform: "translateX(-50%)",
          textAlign: "center", animation: "perryGradePop 0.95s ease-out forwards",
        }}>
          <div style={{
            fontFamily: GM.fontD, fontSize: grade.g === "FRACTURE" ? 40 : 46,
            fontWeight: 700, letterSpacing: 5,
            color: (GRADE_STYLE[grade.g] || GRADE_STYLE.MISS).c,
            textShadow: `0 0 26px ${(GRADE_STYLE[grade.g] || GRADE_STYLE.MISS).c}, 0 2px 6px #000`,
          }}>
            {(GRADE_STYLE[grade.g] || GRADE_STYLE.MISS).label}
          </div>
          {(GRADE_STYLE[grade.g] || {}).sub && (
            <div style={{ fontSize: 11, letterSpacing: 4, color: GM.vellumDim }}>
              {GRADE_STYLE[grade.g].sub.toUpperCase()}
            </div>
          )}
        </div>
      )}

      {/* ── THE GRIMOIRE RACK — the five you took ── */}
      <PowerRack director={director} />

      {/* ── THE KEY ROSE — training wheels, and honest about it ── */}
      <KeyRose director={director} />

      {/* ── SUBTITLE ──
          `line.who` arrived 2026-07-30 with the Sheathe. A line
          with no speaker is the ROAD — the narrator this game has
          always had, unattributed and unchanged. A line WITH one
          is somebody talking over the top of the fight, and it is
          set differently so a player never has to wonder which
          they are reading. The road does not use quote marks; a
          person does.

          `VOICE` is a map, not a ternary chain — a fourth speaker
          is a row in SHEATHE_LINES and a row here. */}
      {line && (
        <div key={line.at} style={{
          position: "absolute", bottom: 96, left: "50%", transform: "translateX(-50%)",
          width: "min(760px, 82%)", textAlign: "center",
          animation: "perrySubIn 0.45s ease-out",
        }}>
          {line.who && VOICE[line.who]?.label && (
            <div style={{
              fontSize: 9, letterSpacing: 4, marginBottom: 4,
              color: `${VOICE[line.who].tint}99`,
              textShadow: "0 2px 8px #000",
            }}>{VOICE[line.who].label}</div>
          )}
          <div style={{
            fontFamily: GM.fontBody,
            fontSize: line.who === "world" ? 16 : 19,
            lineHeight: 1.5,
            fontStyle: line.who === "world" ? "italic" : "normal",
            color: line.who ? (VOICE[line.who]?.tint || GM.vellum) : GM.vellum,
            opacity: line.who === "world" ? 0.82 : 1,
            textShadow: "0 2px 8px #000, 0 0 22px rgba(0,0,0,0.9)",
          }}>{line.who && line.who !== "world" ? `“${line.text}”` : line.text}</div>
        </div>
      )}

      {/* ── THE TUTORIAL BANNER — caP'n, 2026-07-29 ── */}
      <TutorialBanner director={director} />

      {/* ── THE REPORT CARD — caP'n, 2026-07-30 ──
          Last in the tree and highest in z, because while it is up
          the road is paused and nothing behind it is asking to be
          read. It is the only thing in this file that is allowed
          to cover the creature. */}
      <ReportCard card={director.card} />

      {/* ── BEAT TITLE CARD ── */}
      {director.beat?.type === "title" && (
        <div style={{
          position: "absolute", inset: 0, display: "flex",
          flexDirection: "column", alignItems: "center", justifyContent: "center",
          animation: "perrySubIn 0.9s ease-out",
        }}>
          <div style={{
            fontFamily: GM.fontD, fontSize: 44, letterSpacing: 9, color: GM.vellum,
            textShadow: `0 0 34px ${GM.inkGold}, 0 3px 10px #000`,
          }}>{director.beat.text}</div>
          <MetalRule width={340} style={{ margin: "16px auto 12px" }} />
          {director.beat.sub && (
            <div style={{ fontSize: 13, letterSpacing: 6, color: GM.vellumDim }}>
              {director.beat.sub.toUpperCase()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  THE TUTORIAL BANNER — the promise, and the door out
// ══════════════════════════════════════════════════════════════
// Two facts, stated once, standing for the whole road:
//
//   YOU CANNOT FALL HERE — because a player who does not know
//   that plays the tutorial like a real road, learns nothing from
//   dying, and never finds out that the report card exists.
//   Immortality that nobody is told about is just a bug they have
//   not noticed yet.
//
//   ESC — SKIP — because caP'n asked for a skip that works "even
//   on a new game," and an affordance nobody can see is not one.
//
// It renders NOTHING on any act that is not a tutorial, and the
// whole thing hangs off two fields on the act row — so a later
// act that wants either half gets it by saying so in data.
//
// It is allowed to re-render (it reads `hud.struck`, which only
// moves on a discrete event — a blow landing). That is exactly
// the kind of thing React is for, and it is why the tally is on
// the HUD rather than polled off a ref like the rose is.
function TutorialBanner({ director }) {
  if (!director.tutorial) return null;
  const struck = director.hud?.struck || 0;
  const spared = director.hud?.spared || 0;
  const canSkip = !!director.act?.skippable;
  const asking = !!director.skipAsk;
  return (
    <div style={{
      position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)",
      textAlign: "center", pointerEvents: "none", fontFamily: GM.fontUI,
    }}>
      <div style={{
        fontSize: 9, letterSpacing: 5, color: GM.inkGoldBright,
        textShadow: "0 0 14px rgba(0,0,0,0.9)",
      }}>{TUTORIAL.banner}</div>
      <div style={{
        fontSize: 8, letterSpacing: 3, color: GM.vellumFaint, marginTop: 2,
      }}>{TUTORIAL.bannerSub}</div>

      {/* The count, and only once there is one. A counter that
          spends most of its life reading 0 teaches nobody
          anything — same law as the Perfect Streak. */}
      {struck > 0 && (
        <div key={struck} style={{
          fontFamily: GM.fontD, fontSize: 15, letterSpacing: 2,
          color: "#e0763a", marginTop: 4,
          animation: "perryGradePop 0.4s ease-out",
        }}>
          STRUCK {struck}
        </div>
      )}

      {/* ── THE RUNNING TALLY, 2026-07-30 ──
          The one the card prints in full afterwards, kept live on
          the glass in between. It is deliberately the ROSE colour
          rather than the ember: this is the number that would have
          ended the run, and it should not read as a score. */}
      {spared > 0 && (
        <div key={`sp${spared}`} style={{
          fontFamily: GM.fontD, fontSize: 13, letterSpacing: 2,
          color: GM.rose, marginTop: 1,
          animation: "perryGradePop 0.5s ease-out",
        }}>
          SPARED {spared}
        </div>
      )}

      {/* ── THE DOOR ──
          One tap arms it and says so; the second tap opens it. A
          single ESC ending the road by accident is a bug report
          waiting to be filed, and ESC is the key every other
          screen in this title closes with. */}
      {canSkip && !asking && (
        <div style={{
          fontSize: 8, letterSpacing: 3, color: "#6a5040", marginTop: 6,
        }}>{TUTORIAL.skipHint}</div>
      )}
      {canSkip && asking && (
        <div style={{ marginTop: 6, animation: "perrySkipPulse 1.1s ease-in-out infinite" }}>
          <div style={{
            fontSize: 9, letterSpacing: 4, color: GM.inkGoldBright,
          }}>{TUTORIAL.skipArm}</div>
          <div style={{
            fontSize: 8, letterSpacing: 2, color: GM.vellumFaint, marginTop: 2,
          }}>{TUTORIAL.skipArmSub}</div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//  THE REPORT CARD — the bill for a road that cannot charge you
// ══════════════════════════════════════════════════════════════
// caP'n, 2026-07-30: "we had a pop up after every fight telling
//  you how many times you could've died given your Anima."
//
// THIS IS WHY THE DEATHLESSNESS IS ALLOWED TO EXIST. A road that
// cannot kill you and never mentions it teaches that nothing
// matters. A road that cannot kill you and then hands you the
// number teaches the exact thing the fen is about to charge for.
// The mercy is the delivery mechanism; the number is the lesson.
//
// TWO NUMBERS, NOT ONE, and they are different facts:
//   BLOWS THAT REACHED YOU        — how leaky your guard was
//   TIMES YOU WOULD HAVE FALLEN   — how close the leak came to
//                                   ending it
// Hit nine times and dead twice is a completely different beb
// from hit twice and dead twice, and one number cannot say which
// one you are.
//
// The road is genuinely PAUSED behind this — the director stops
// its loop and moves every live deadline forward by however long
// you stood here. So this may take as long as it likes, and it
// eats any key rather than hunting for the right one.
function ReportCard({ card }) {
  if (!card) return null;
  const clean = !card.struck && !card.spared;
  const tally = card.spared;
  const verdict = clean ? TUTORIAL.cardClean
    : tally === 0 ? TUTORIAL.cardClean
      : tally === 1 ? TUTORIAL.cardOne
        : TUTORIAL.cardMany;
  return (
    <div style={{
      position: "absolute", inset: 0, zIndex: 70,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "radial-gradient(ellipse at 50% 50%, rgba(30,12,6,0.72) 0%, rgba(3,2,2,0.93) 72%)",
      animation: "perryCardVeil 0.42s ease-out",
      fontFamily: GM.fontUI, pointerEvents: "none",
    }}>
      <div style={{
        width: "min(430px, 88vw)", padding: "26px 28px 20px", borderRadius: 13,
        textAlign: "center",
        background: "linear-gradient(175deg, #1c1109 0%, #120a08 62%, #0a0605 100%)",
        border: `1px solid ${tally > 0 ? GM.roseDim : GM.inkGold}`,
        boxShadow: "0 24px 56px rgba(0,0,0,0.86)",
        animation: "perryCardIn 0.5s cubic-bezier(0.2,0.8,0.2,1) 0.18s backwards",
      }}>
        <div style={{
          fontFamily: GM.fontD, fontSize: 21, letterSpacing: 6,
          color: tally > 0 ? GM.rose : GM.vellum,
          textShadow: `0 0 24px ${tally > 0 ? GM.roseDim : GM.inkGold}`,
        }}>{clean ? TUTORIAL.cardTitleClean : TUTORIAL.cardTitle}</div>

        {card.foe && (
          <div style={{
            fontSize: 8, letterSpacing: 4, color: GM.vellumFaint, marginTop: 5,
          }}>{card.broke ? "BROKEN" : "IT WALKED AWAY"} · {card.foe.toUpperCase()}</div>
        )}

        <MetalRule width={180} style={{ margin: "13px auto 15px" }} />

        {/* THE NUMBER. Large, alone, and the only thing on this
            card anybody will remember five minutes later. */}
        <div key={tally} style={{
          fontFamily: GM.fontD, fontSize: 52, lineHeight: 1,
          color: tally > 0 ? GM.rose : GM.vellum,
          textShadow: `0 0 30px ${tally > 0 ? GM.roseDim : GM.inkGold}`,
          animation: "perryTallyIn 0.55s cubic-bezier(0.2,0.9,0.2,1) 0.3s backwards",
        }}>{tally}</div>
        <div style={{
          fontSize: 8, letterSpacing: 4, color: GM.vellumFaint, marginTop: 5,
        }}>{TUTORIAL.cardSpared}</div>

        <div style={{
          fontSize: 9, letterSpacing: 3, color: "#8a7a68", marginTop: 13,
        }}>{TUTORIAL.cardStruck} — {card.struck}</div>

        {card.totalSpared > tally && (
          <div style={{
            fontSize: 8, letterSpacing: 3, color: "#6a5040", marginTop: 3,
          }}>{card.totalSpared} THIS SOLREND</div>
        )}

        <div style={{
          fontFamily: GM.fontBody, fontSize: 14, lineHeight: 1.6,
          color: GM.vellumDim, margin: "15px 4px 0",
        }}>{verdict}</div>

        <div style={{
          fontSize: 8, letterSpacing: 5, color: "#5c4636", marginTop: 17,
        }}>{TUTORIAL.cardGo}</div>
      </div>
    </div>
  );
}

function Vital({ label, hint, children }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
      <div style={{ width: 54, textAlign: "right" }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: GM.vellumFaint }}>{label}</div>
      </div>
      <div title={hint}>{children}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// THE GRIMOIRE RACK — five slots, five keys, no future in any of it
// ══════════════════════════════════════════════════════════════
// Replaces the old three-Impera bar, 2026-07-28. It draws the five
// powers you kitted up with, in slot order, and it is allowed to
// show four things about each: what it is, what it costs, whether
// you can afford it RIGHT NOW, and how many charges of it are
// currently armed.
//
// Every one of those is a fact about the present or the past. None
// of them is a fact about the next strike. That is the line this
// whole file exists to hold, and a power rack is exactly the kind
// of thing that would quietly cross it — an "incoming!" pip on the
// Mirage would be enormously helpful and would delete the game.
//
// IT NEVER RE-RENDERS. Same law as the KeyRose beneath it: it
// polls the director's refs on its own rAF and writes styles
// imperatively. Charges tick, clocks run down, affordability
// flickers as Chao moves — and not one of those costs a React
// render, because a parry must never wait on one.
function PowerRack({ director }) {
  const cells = useRef({});
  const raf = useRef(0);
  const slots = director.slots || [];

  useEffect(() => {
    const tick = () => {
      raf.current = requestAnimationFrame(tick);
      const P = director.powers;
      const hud = director.hud || {};
      if (!P) return;
      const t = performance.now();

      // How many of THIS power are currently armed. A string
      // switch, not a lookup table keyed by id — an eleventh power
      // that arms nothing simply shows nothing, with no edit here.
      const armed = (fx) => {
        if (fx === "shadow") return P.shadow;
        if (fx === "echo") return P.echo;
        if (fx === "mirage") return P.mirage;
        if (fx === "answer") return P.answer;
        if (fx === "grace") return P.graceUntil && t < P.graceUntil ? Math.ceil((P.graceUntil - t) / 1000) : 0;
        if (fx === "emperium") return P.emp.until && t < P.emp.until ? P.emp.left : 0;
        return 0;
      };
      const unit = (fx) => (fx === "grace" ? "s" : "");

      for (let i = 0; i < slots.length; i++) {
        const el = cells.current[i];
        const id = slots[i];
        if (!el || !id) continue;
        const p = REGALIA[id];
        if (!p) continue;

        // The Crown and the Pool both pay for a power you cannot
        // afford, so both make it lit. Showing it dim while it is
        // genuinely available would teach the player not to press
        // the thing they paid 45 Kindling for.
        const free = P.crownLeft > 0 || (P.poolLeft > 0 && hud.chao < p.costChao);
        const afford = (free || hud.chao >= p.costChao) && hud.anima > p.costAnima;
        const n = armed(p.fx);

        el.style.opacity = afford || n ? "1" : "0.4";
        el.style.borderColor = n ? "#4ff0dc" : afford ? GM.inkGoldDim : "#2a1e16";
        el.style.boxShadow = n ? "0 0 14px rgba(79,240,220,0.35)" : "none";

        const badge = cells.current[`b${i}`];
        if (badge) {
          badge.textContent = n ? `${n}${unit(p.fx)}` : "";
          badge.style.opacity = n ? "1" : "0";
        }
        const cost = cells.current[`c${i}`];
        if (cost) {
          cost.style.color = free ? "#4ff0dc" : GM.vellumFaint;
          cost.textContent = free ? "FREE" : costOf(p);
        }
      }
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [director, slots]);

  if (!slots.some(Boolean)) return null;

  return (
    <div style={{
      position: "absolute", bottom: 78, left: 24, display: "flex", gap: 8,
    }}>
      {slots.map((id, i) => {
        const p = id ? REGALIA[id] : null;
        if (!p) return null;
        return (
          <div
            key={i}
            ref={(el) => { cells.current[i] = el; }}
            style={{
              position: "relative",
              display: "flex", alignItems: "center", gap: 7,
              padding: "5px 10px 5px 6px", borderRadius: 8,
              background: "linear-gradient(175deg, #1c1009, #100906)",
              border: `1px solid ${GM.inkGoldDim}`,
              transition: "opacity 0.18s ease-out, border-color 0.18s ease-out",
            }}
          >
            <KeyGlyph k={slotKey(i)} size={24} active />
            <div>
              <div style={{ fontSize: 11, color: GM.vellum, letterSpacing: 1, whiteSpace: "nowrap" }}>
                {p.icon} {p.name}
              </div>
              <div
                ref={(el) => { cells.current[`c${i}`] = el; }}
                style={{ fontSize: 9, color: GM.vellumFaint, letterSpacing: 1 }}
              >{costOf(p)}</div>
            </div>
            {/* the armed count — a fact about what you already did */}
            <div
              ref={(el) => { cells.current[`b${i}`] = el; }}
              style={{
                position: "absolute", top: -7, right: -6, opacity: 0,
                minWidth: 17, height: 17, padding: "0 4px", borderRadius: 9,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 700, color: "#032a26",
                background: "#4ff0dc", boxShadow: "0 0 10px rgba(79,240,220,0.7)",
                transition: "opacity 0.15s ease-out",
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

// One line of cost, assembled from the data so a new power can
// never ship with a stale label. (Same discipline as treasureLine.)
function costOf(p) {
  const bits = [];
  if (p.costChao) bits.push(`${p.costChao} CHAO`);
  if (p.costAnima) bits.push(`${p.costAnima} ANIMA`);
  return bits.join(" + ") || "FREE";
}

// ══════════════════════════════════════════════════════════════
// THE EMPERIUM VEIL
// ══════════════════════════════════════════════════════════════
// caP'n: "landscape completely changes to their character's
// theme/style... time limit of 20 seconds so if not parrys in that
// time they're gone, not saved."
//
// The landscape half is real and lives in the 3D stage — the
// director writes a biome string and `CinematicStage` already
// switches worlds on one. THIS is the other half: a full-frame
// wash in the species' own colour, a countdown, and the three
// answers going out one at a time.
//
// It is deliberately the cheapest possible version of the idea and
// it is deliberately enough for now. caP'n, same message: "visuals
// gonna look..not good at first I know don't worry about that."
// The hooks are what matter. Every colour it uses comes from
// EMPERIUM_THEMES, so the day the real sky gets built, this file
// does not change — the theme does.
//
// Also self-polling. A twenty-second countdown that re-rendered
// the run overlay sixty times a second would be the single worst
// thing in the codebase.
function EmperiumVeil({ director }) {
  const wrap = useRef(null);
  const ring = useRef(null);
  const pips = useRef(null);
  const clock = useRef(null);
  const title = useRef(null);
  const raf = useRef(0);
  const lastOn = useRef(false);

  useEffect(() => {
    const tick = () => {
      raf.current = requestAnimationFrame(tick);
      const P = director.powers;
      if (!P || !wrap.current) return;
      const t = performance.now();
      const on = !!(P.emp.until && t < P.emp.until);

      if (on !== lastOn.current) {
        lastOn.current = on;
        wrap.current.style.opacity = on ? "1" : "0";
        wrap.current.style.visibility = on ? "visible" : "hidden";
        if (on) {
          const th = P.emp.theme || {};
          wrap.current.style.background =
            `radial-gradient(ellipse at 50% 58%, ${th.deep || "#1c1009"}00 18%, ${th.deep || "#1c1009"}cc 76%, ${th.deep || "#1c1009"}f2 100%)`;
          if (ring.current) ring.current.style.background =
            `linear-gradient(90deg, transparent, ${th.tint || GM.inkGold}, transparent)`;
          if (title.current) {
            title.current.textContent = th.name || "THE EMPERIUM";
            title.current.style.color = th.hot || "#ffe6c2";
            title.current.style.textShadow = `0 0 40px ${th.tint || GM.inkGold}, 0 3px 10px #000`;
            // restart the flourish — the standard reflow trick
            title.current.style.animation = "none";
            void title.current.offsetWidth;
            title.current.style.animation = "perryEmpTitle 2.6s ease-out forwards";
          }
        }
      }
      if (!on) return;

      const left = Math.max(0, P.emp.until - t);
      if (clock.current) {
        clock.current.textContent = (left / 1000).toFixed(1);
        // The last five seconds go red, because "gone, not saved"
        // is only a real cost if the player can feel it coming.
        clock.current.style.color = left < 5000 ? "#f4726a" : (P.emp.theme?.hot || "#ffe6c2");
      }
      if (pips.current) {
        const n = P.emp.left;
        for (let i = 0; i < 3; i++) {
          const el = pips.current.children[i];
          if (!el) continue;
          const lit = i < n;
          el.style.opacity = lit ? "1" : "0.18";
          el.style.transform = lit ? "scale(1)" : "scale(0.72)";
        }
      }
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [director]);

  return (
    <div
      ref={wrap}
      style={{
        position: "absolute", inset: 0, opacity: 0, visibility: "hidden",
        transition: "opacity 0.5s ease-out",
      }}
    >
      <div ref={ring} style={{
        position: "absolute", left: 0, right: 0, top: 0, height: 2, opacity: 0.8,
      }} />

      {/* the name of your own sky, once, on the way in */}
      <div ref={title} style={{
        position: "absolute", left: 0, right: 0, top: "26%", textAlign: "center",
        fontFamily: GM.fontD, fontSize: "clamp(24px, 5vw, 44px)", letterSpacing: 11,
        opacity: 0,
      }} />

      {/* the clock and the three, bottom centre, out of the way of
          everything that matters */}
      <div style={{
        position: "absolute", bottom: 44, left: "50%", transform: "translateX(-50%)",
        display: "flex", alignItems: "center", gap: 14,
      }}>
        <div ref={pips} style={{ display: "flex", gap: 6 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{
              width: 11, height: 11, borderRadius: 6, background: "#4ff0dc",
              boxShadow: "0 0 12px rgba(79,240,220,0.8)",
              transition: "all 0.2s ease-out",
            }} />
          ))}
        </div>
        <div ref={clock} style={{
          fontFamily: GM.fontD, fontSize: 20, letterSpacing: 3, minWidth: 52,
          textAlign: "center", textShadow: "0 2px 8px #000",
        }} />
        <div style={{ fontSize: 9, letterSpacing: 3, color: GM.vellumFaint }}>
          NOTHING IS SAVED
        </div>
      </div>
    </div>
  );
}

// ── THE KEY ROSE ─────────────────────────────────────────────
// The other half of the training pair. `HintRose` in the 3D stage
// marks the six places on Perry's body where the answers live —
// that teaches the SHAPE, in the space you will keep fighting in
// after the coals are gone. This teaches which FINGER, and it is
// laid out in exactly the same geometry, because after the rose
// was re-anchored to the camera plane the two are the same
// picture at two different scales.
//
// It is deliberately small, deliberately in a corner, and
// deliberately temporary: on until Hollowmere, off forever after.
//
// IT NEVER RE-RENDERS. It polls the director's strikeRef on its
// own rAF and writes styles imperatively — the same law every
// other continuous thing in this title obeys. A parry must not
// cost a React render, and neither may the thing teaching you to
// make one.
function KeyRose({ director }) {
  const cells = useRef({});
  const wrap = useRef(null);
  const raf = useRef(0);

  useEffect(() => {
    const tick = () => {
      raf.current = requestAnimationFrame(tick);
      const st = director.strikeRef?.current;
      const sg = director.sightRef?.current;

      // THE CARD IS A LETTER CARD. It appears exactly when letters
      // are legal and never otherwise — at COLOURED tier the world
      // is already saying everything this could say, in colour, in
      // the space you actually fight in. A second copy in a corner
      // would only teach you to look at the corner.
      const named = !!(st && st.named);
      const lettered = named || (sg?.tier || SIGHT.LETTERED) === SIGHT.LETTERED;
      if (wrap.current) {
        wrap.current.style.opacity = lettered ? "0.92" : "0";
        wrap.current.style.visibility = lettered ? "visible" : "hidden";
      }
      if (!lettered) return;

      const liveKey = st && !st.feint ? st.angle : null;
      const lying = !!(st && st.feint);
      for (const k of GUARD.KEYS) {
        const el = cells.current[k];
        if (!el) continue;
        const hue = hueOf(k);
        const hot = k === liveKey;
        // The hue is always the KEY's — that is the whole contract
        // the world rose is teaching, and this card must not tell a
        // different story about it. Lit changes VALUE, never hue.
        el.style.borderColor = hot ? hue : lying ? GM.roseDim : "#2a1e16";
        el.style.color = hot ? "#fff6e4" : hue;
        el.style.background = hot
          ? `linear-gradient(175deg, ${hue}, #2a1608)`
          : "linear-gradient(175deg, #1c1009, #100906)";
        el.style.boxShadow = hot ? `0 0 16px ${hue}aa` : "none";
        el.style.transform = hot ? "scale(1.14)" : "scale(1)";
        el.style.opacity = hot ? "1" : "0.72";
      }
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [director]);

  const cell = (k) => (
    <div
      key={k}
      ref={(el) => { cells.current[k] = el; }}
      style={{
        width: 30, height: 30, borderRadius: 7,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: GM.fontUI, fontSize: 13, fontWeight: 700, letterSpacing: 1,
        border: `1px solid ${hueOf(k)}55`, color: hueOf(k),
        background: "linear-gradient(175deg, #1c1009, #100906)",
        transition: `all ${QTE.HINT_FADE_MS}ms ease-out`,
      }}
    >{k}</div>
  );

  return (
    <div ref={wrap} style={{
      position: "absolute", right: 26, bottom: 92,
      display: "flex", flexDirection: "column", gap: 6, alignItems: "center",
      opacity: 0, visibility: "hidden",
      transition: `opacity ${QTE.HINT_FADE_MS}ms ease-out`,
    }}>
      {/* THREE ROWS OF TWO as of 2026-07-27 — the corner rose has
          to be the same shape as the one in the world, or it is
          teaching the wrong thing. It also has to be the same
          COLOURS, for the same reason: the colours outlive it. */}
      {GUARD.ROWS.map((row, i) => (
        <div key={i} style={{ display: "flex", gap: 6 }}>{row.map(cell)}</div>
      ))}
      <div style={{ fontSize: 8, letterSpacing: 3, color: GM.vellumFaint, marginTop: 2 }}>
        LEARN THE COLOURS
      </div>
    </div>
  );
}
